import type {
  ContextWindowSegments,
  ContextWindowUsage,
} from "@shaarp/common";
import { formatPercent } from "./format";

export type SegmentKey = keyof ContextWindowSegments;

export type SegmentRow = {
  key: SegmentKey;
  /**
   * Translation keys rather than text: this module is pure, and resolving them
   * here would need a hook it cannot call. The panel and the composer's hover
   * card each pick the length they have room for.
   */
  labelKey: string;
  shortLabelKey: string;
  hintKey: string;
  color: string;
  tokens: number;
};

/**
 * The blocks in the order the turn assembles them, so the bar reads like the
 * prompt: instructions first, conversation last, what is left at the end.
 */
const SEGMENT_ORDER: readonly SegmentKey[] = [
  "system",
  "tools",
  "memory",
  "knowledge",
  "tactical",
  "history",
  "lastMessage",
  "free",
  // Last, and only ever drawn when it is not zero: it is what the prompt asks
  // for past the end of the bar.
  "overflow",
];

/** Literal colours, as the rest of the charts in this codebase do. */
const SEGMENT_COLORS: Record<SegmentKey, string> = {
  system: "hsl(219, 83%, 40%)",
  tools: "hsl(250, 97%, 62%)",
  memory: "hsl(280, 65%, 60%)",
  knowledge: "hsl(190, 80%, 45%)",
  tactical: "hsl(38, 92%, 50%)",
  history: "hsl(160, 60%, 45%)",
  lastMessage: "hsl(122, 40%, 50%)",
  free: "hsl(215, 20%, 88%)",
  overflow: "hsl(0, 72%, 51%)",
};

/**
 * The breakdown as rows to render, in prompt order.
 *
 * Empty blocks are dropped — a conversation with no knowledge base should not
 * show a legend entry worth nothing — except `free`, which is the one row whose
 * being zero is the point.
 *
 * @param segments The breakdown the API persisted, or none for an older message.
 * @returns One row per non-empty block, or an empty list when there is no breakdown.
 */
export const toSegmentRows = (
  segments: ContextWindowSegments | undefined
): SegmentRow[] => {
  if (!segments) return [];

  return SEGMENT_ORDER.map((key) => ({
    key,
    labelKey: `chat.debug.segments.${key}.label`,
    shortLabelKey: `chat.debug.segments.${key}.short`,
    hintKey: `chat.debug.segments.${key}.hint`,
    color: SEGMENT_COLORS[key],
    tokens: segments[key],
  })).filter((row) => row.key === "free" || row.tokens > 0);
};

export type ContextGap = {
  breakdownUsed: number;
  /** Breakdown minus the estimate the compaction runs on. */
  delta: number;
  breakdownPercent: string;
  estimatePercent: string;
};

/**
 * The distance between the two token counts of the same turn.
 *
 * Both come from the same estimator, so the gap is narrow and explainable: the
 * breakdown also bills the tool definitions, which travel beside the messages
 * rather than inside them, and it takes the interpolated blocks out of the
 * system prompt. A wide gap therefore points at a turn whose tool catalogue is
 * a real share of the window — worth seeing rather than hiding.
 *
 * @returns `null` for a message with no breakdown, where there is nothing to compare.
 */
export const toContextGap = (usage: ContextWindowUsage): ContextGap | null => {
  const { segments, contextWindowSize, estimatedTokensUsed } = usage;
  if (!segments) return null;

  // `overflow` included: without it this stops at the window size the moment a
  // prompt goes past it, hiding the gap exactly when it is worth seeing.
  // Defaulted, because a message persisted before the field existed carries a
  // breakdown without it — and `undefined` here would print the whole panel as
  // `NaN`.
  const breakdownUsed =
    contextWindowSize - segments.free + (segments.overflow ?? 0);

  return {
    breakdownUsed,
    delta: breakdownUsed - estimatedTokensUsed,
    breakdownPercent: formatPercent(breakdownUsed, contextWindowSize),
    estimatePercent: formatPercent(estimatedTokensUsed, contextWindowSize),
  };
};
